import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, IOcbApiDescriptionParameter, IOcbEnvironmentApiKey } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxValidationGroupComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { forkJoin, from as fromPromise, Observable } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-apikeys',
    templateUrl: './api-keys.component.html',
    styleUrls: ['./api-keys.component.scss'],
})
export class ApiKeysComponent implements AfterViewInit {

    @ViewChild('apiKeys')
    public apiKeysGrid: DxDataGridComponent;

    @ViewChild('apiKeyValidation')
    public apiKeyValidationGroup: DxValidationGroupComponent;

    // Main Data-Grid
    public dataSource: IOcbEnvironmentApiKey[] = null;

    // Popup New & Edit
    public isEditPopupVisible: boolean = false;
    public selectedApiKey: IOcbEnvironmentApiKey = null;
    public isNew: boolean = null;

    /**
     * Boolean to show or hide the loading panel
     */
    public isLoadPanelVisible: boolean = false;

    /**
     * Internal identifier of the current environment
     */
    private currentEnvironmentId: string = '';

    constructor(
        private route: ActivatedRoute,
        private environmentsService: EnvironmentsService,
        private translateService: TranslateService) {
    }

    public ngAfterViewInit() {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.loadDataSource();
        });
    }

    /**
     * Set Recipients context menu items
     * @param e Handle event
     */
    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin(
                // Get translation messages
                this.translateService.get('_commons.rename'),
                this.translateService.get('_commons.reset'),
                this.translateService.get('_commons.delete'),
            ).subscribe(
                messages => {
                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.editApiKey(ev, e.row.data) },
                        { text: messages[1], icon: 'pulldown', onItemClick: (ev) => this.resetApiAkey(ev, e.row.data.id) },
                        { text: messages[2], icon: 'trash', onItemClick: (ev) => this.deleteApiKey(ev, e.row.data.id) },
                    ];
                },
                err => this.handleError(err),
            );
        }
    }

    /**
     * Display update popup
     * @param e Handle event
     */
    public onRowDblClick(e) {
        if (e && e.data) {
            this.editApiKey(e, e.data);
        }
    }

    /**
     * Dipslay new popup
     */
    public newApiKey(): void {
        this.selectedApiKey = {};
        this.isNew = true;
        this.isEditPopupVisible = true;
    }

    /**
     * Display update popup
     * @param e Handle event
     * @param apiKey API key data to update
     */
    public editApiKey(e, apiKey: IOcbEnvironmentApiKey): void {
        if (e && e.event) {
            e.event.stopPropagation();
        }

        this.selectedApiKey = {
            id: apiKey.id,
            environmentId: apiKey.environmentId,
            environmentName: apiKey.environmentName,
            description: apiKey.description,
            apiKey: apiKey.apiKey,
        };
        this.isNew = false;
        this.isEditPopupVisible = true;
    }

    /**
     * Create an API key
     */
    public createApiKey(): void {
        if (this.apiKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newApiKey: IOcbApiDescriptionParameter = { description: this.selectedApiKey.description };
            this.environmentsService.addEnvironmentApiKey(this.currentEnvironmentId, newApiKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('apiKeys.msgApiKeyCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(
                    message => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    err => this.handleError(err));
        }
    }

    /**
     * Update an API key
     */
    public updateApiKey(): void {
        if (this.apiKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newDescription: IOcbApiDescriptionParameter = { description: this.selectedApiKey.description };
            this.environmentsService.updateEnvironmentApiKey(this.currentEnvironmentId, this.selectedApiKey.id, newDescription)
                .pipe(
                    mergeMap(_ => this.translateService.get('apiKeys.msgApiKeyUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe(
                    message => {
                        this.isEditPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    err => this.handleError(err));
        }
    }

    /**
     * Delete an API key
     * @param e Handle event
     * @param id Internal identifier of the API Key
     */
    public deleteApiKey(e, id): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.translateService.get('apiKeys.msgDeleteConfirm'),
            this.translateService.get('apiKeys.msgDeleteConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.environmentsService.deleteEnvironmentApiKey(this.currentEnvironmentId, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('apiKeys.msgDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                message => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                }, err => this.handleError(err),
            );
    }

    /**
     * Reset an API key
     * @param e Handle event
     * @param id Internal identifier of the API Key
     */
    public resetApiAkey(e, id): void {
        e.event.stopPropagation();
        forkJoin(
            // Get translation messages
            this.translateService.get('apiKeys.msgResetConfirm'),
            this.translateService.get('apiKeys.msgResetConfirmTitle'))
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the DELETE request
                mergeMap(_ => this.environmentsService.resetEnvironmentApiKey(this.currentEnvironmentId, id)),
                // Get the confirmation message
                mergeMap(_ => this.translateService.get('apiKeys.msgResetComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe(
                message => {
                    this.loadDataSource();
                    // Notify the success
                    notify(message, 'success', 3000);
                },
                err => this.handleError(err));
    }

    /**
     * Display a success notification to the user
     */
    public notifyCopy(): void {
        this.translateService.get('_clipboard.copied')
            .subscribe(message => {
                notify(message, 'success', 3000);
            });
    }

    /**
     * Load data source with the current environment API keys
     */
    private loadDataSource(): void {
        this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.environmentsService.getEnvironmentApiKeys(this.currentEnvironmentId)),
                finalize(() => this.apiKeysGrid.instance.endCustomLoading()))
            .subscribe(
                res => this.dataSource = res,
                err => this.handleError(err));
    }

    /**
     * Displays a loader over the data grid, with a localized message.
     * @param messageKey Localized message key.
     */
    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.apiKeysGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    /**
     * Displays error message in notification
     */
    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
